import { Box, Stack, SvgIcon, Typography } from "@mui/material";
import {
  ConversationBasicData,
  TaskPatientData,
  TaskState,
  useLazyGetConversationDetailQuery,
} from "../features/data/patientApiSlice";
import { createStyleSheet, globalStyles } from "../utils/styles";

import ConcernPillList from "./ConcernPillList";
import ConversationIcon from "../../assets/event_conversation_summary.svg?react";
import EntryContainer from "./EntryContainer";
import TaskEntry from "./TaskEntry";
import { formatDateTime } from "../utils/dates";
import { getErrorText } from "../utils/errors";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export const getConversationElementId = (uid: string) => `conversation-${uid}`;

const ConversationEntry = (props: {
  conversationData: ConversationBasicData;
  expandByDefault?: boolean;
  tasks: TaskPatientData[];
  selectedTaskUid?: string;
  onSelectTask?: (taskUid: string) => void;
  onAddTaskNote?: (taskUid: string, content: string) => void;
  onCompleteTask?: (completeData: { actionsTaken: string[]; notes: string }, task: TaskPatientData) => void;
  onDeleteTask?: (task: TaskPatientData) => void;
}) => {
  const {
    conversationData,
    expandByDefault,
    tasks,
    selectedTaskUid,
    onSelectTask,
    onAddTaskNote,
    onCompleteTask,
    onDeleteTask,
  } = props;
  const { t } = useTranslation("ConversationEntry");

  // Queries/mutations
  const [getConversationDetail, conversationDetailResult] =
    useLazyGetConversationDetailQuery();

  // Callbacks
  const handleExpanded = useCallback(
    () => getConversationDetail({ uid: conversationData.uid }, true),
    [getConversationDetail, conversationData.uid]
  );

  const taskState =
    tasks.length > 0
      ? tasks.some((task) => task.state === TaskState.todo)
        ? TaskState.todo
        : TaskState.complete
      : undefined;
  const activeTaskCount = tasks.filter(
    (task) => task.state === TaskState.todo
  ).length;
  return (
    <EntryContainer
      id={getConversationElementId(conversationData.uid)}
      containing
      titleIcon={
        <SvgIcon
          component={ConversationIcon}
          inheritViewBox
          fontSize="large"
          color={taskState === TaskState.todo ? "primary" : "action"}
        />
      }
      titleText={t("title")}
      titleTextPost={
        activeTaskCount > 0
          ? t("common:activeTasks", { count: activeTaskCount })
          : undefined
      }
      subtitleText={formatDateTime(new Date(conversationData.timestamp))}
      extraTitleComponent={
        <ConcernPillList tasks={tasks} includeRequestToChat />
      }
      expandByDefault={expandByDefault}
      onExpanded={handleExpanded}
      taskStateIndicator={taskState}
      isFetching={conversationDetailResult.isFetching}
      errorText={
        conversationDetailResult.isError
          ? getErrorText(conversationDetailResult.error)
          : ""
      }
      bodyComponent={
        <Stack direction="column">
          {conversationDetailResult.data ? (
            <Stack direction="column">
              <Box sx={styles.detailBackground}>
                <Box sx={styles.detailTextContainer}>
                  <Typography variant="detailEntryBody">
                    {conversationDetailResult.data.summary?.summary ||
                      t("noSummary")}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          ) : null}
          {tasks.map((task) => (
            <TaskEntry
              key={task.uid}
              contained
              task={task}
              selected={task.uid === selectedTaskUid}
              expandByDefault={true}
              onSelect={onSelectTask}
              onAddNote={onAddTaskNote}
              onComplete={onCompleteTask}
              onDelete={onDeleteTask}
            />
          ))}
        </Stack>
      }
    />
  );
};

const styles = createStyleSheet({
  detailBackground: {
    ...globalStyles.gapTop,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: (theme) => theme.palette.detailBackground.main,
    borderRadius: 1,
    px: 2,
    py: 1,
  },
  detailTextContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
    gap: 0.5,
  },
  buttonIcon: {
    marginLeft: 1,
  },
});

export default ConversationEntry;
